import { IApiErrorData } from "api/axios/types";
import {
  IApproveOrRejectIndividualSectionPayload,
  IApproveOrRejectIndividualSectionResponse,
  TIndividualSectionType,
  TIndividualSectionType2,
} from "api/compliance/types";
import { useIndividualCompliance } from "contexts/individual-compliance-context";
import { IndividualComplianceQueryKeys } from "contexts/individual-compliance-context/consts";
import { capitalizeFirstLetter } from "utils";

import { customErrorToast, customSuccessToast } from "ui/custom-toast";

import { useMutateData } from "./useMutateData";

export type TIndividualSectionTitle =
  | "User Details"
  | "BVN and NIN Information"
  | "Face and ID Verification"
  | "Address Verification";

export const individualSectionType: Record<
  TIndividualSectionTitle,
  TIndividualSectionType
> = {
  "User Details": "user-details",
  "BVN and NIN Information": "bvn-and-nin-information",
  "Face and ID Verification": "face-and-id-verification",
  "Address Verification": "address-verification",
};

export const individualSectionTypeToLabelMap: Record<
  TIndividualSectionType,
  TIndividualSectionTitle
> = {
  "user-details": "User Details",
  "bvn-and-nin-information": "BVN and NIN Information",
  "face-and-id-verification": "Face and ID Verification",
  "address-verification": "Address Verification",
};

export const individualSectionTypeToLabelMap2: Record<
  TIndividualSectionType2,
  TIndividualSectionTitle
> = {
  "User Details": "User Details",
  "BVN and NIN Information": "BVN and NIN Information",
  "Face Verification": "Face and ID Verification",
  "Address Verification": "Address Verification",
};

export interface IUseApproveOrRejectIndividualSectionArgs {
  individualId: string;
  individualSectionTitle: TIndividualSectionTitle;
}

export interface IUseApproveOrRejectIndividualSectionReturnData {
  approveIndividualSection: () => Promise<void>;
  rejectIndividualSection: (reason: string) => Promise<void>;
  isApprovingOrRejecting: boolean;
}

export const useApproveOrRejectIndividualSection = (
  args: IUseApproveOrRejectIndividualSectionArgs
): IUseApproveOrRejectIndividualSectionReturnData => {
  const { individualId, individualSectionTitle } = args;
  const { handleFetchIndividualDetails } = useIndividualCompliance();

  const {
    mutateAsync: handleApproveOrRejectSection,
    isPending: isApprovingOrRejecting,
  } = useMutateData<
    IApproveOrRejectIndividualSectionPayload,
    IApproveOrRejectIndividualSectionResponse
  >([IndividualComplianceQueryKeys.INDIVIDUAL_DETAILS], {
    onSuccess: async (data, payload) => {
      customSuccessToast({
        title: `${individualSectionTitle} ${capitalizeFirstLetter(payload.data.status)}`,
        message: data.message,
      });
      await handleFetchIndividualDetails(individualId);
    },
    onError(error) {
      const err = error as unknown as IApiErrorData;
      const message = err.message;
      customErrorToast({ message });
    },
  });

  const approveIndividualSection = async () => {
    try {
      await handleApproveOrRejectSection({
        apiRoute: `/admin/compliance-section-update/${individualId}`,
        data: {
          type: individualSectionType[individualSectionTitle],
          status: "approved",
          reason: "",
        },
        method: "POST",
        module: "EARNIPAY_API",
        useEncryption: true,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const rejectIndividualSection = async (reason: string) => {
    try {
      await handleApproveOrRejectSection({
        // apiRoute: `/admin/compliance-section-update/${individualId}`,
        apiRoute: `/control/personal/compliance/users/${individualId}`,
        data: {
          type: individualSectionType[individualSectionTitle],
          status: "rejected",
          reason,
        },
        method: "POST",
        module: "EARNIPAY_API",
        useEncryption: true,
      });
    } catch (error) {
      console.error(error);
    }
  };

  return {
    approveIndividualSection,
    rejectIndividualSection,
    isApprovingOrRejecting,
  };
};
